import React, { useState, useEffect, useCallback } from 'react'
import useAxios, { mutate } from 'hooks/useAxios'
import * as R from 'ramda'
import { useHistory, useLocation } from 'react-router-dom'

import { toast } from 'libs/toast'
import EmbeddingCyclrIntegration from '.'
import useQueryParams from 'hooks/useQueryParams'

import type { IntegrationTemplate } from '.'
import { Loader } from 'shared/components/Loader'
import { parseErrorMessage } from 'libs/parseErrorMessage'
import useSaveUserActivityLog, { INTEGRATION_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useUser from 'hooks/useUser'

export default function EmbeddingCyclrIntegrationContainer({
  connectorId,
}: {
  connectorId: string
}) {
  const [isAuthenticateSuccessful, setIsAuthenticateSuccessful] = useState(false)
  const location = useLocation()
  const history = useHistory()
  const queryParams = useQueryParams()
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  const { data: connector } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/`,
    skip: !connectorId,
  })
  const connectorLabel = R.prop('connector_label', connector) || ''

  const { fetch: callBasicAuthenication, loading: isBasicAuthenticating } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/basic-auth/`,
    method: 'post',
    skip: true,
  })

  const { fetch: callApiKeyAuthenication, loading: isApiKeyAuthenticating } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/api-key/`,
    method: 'post',
    skip: true,
  })

  const {
    fetch: callOauth2PasswordCredentialAuthen,
    loading: isOauth2PasswordCredentialAuthenticating,
  } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/oauth2-password-credential/`,
    method: 'post',
    skip: true,
  })

  const { fetch: callNoneAuthenication, loading: isNoneAuthenticating } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/none/`,
    method: 'post',
    skip: true,
  })

  const { fetch: callRevokeAccess, loading: isRevoking } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/deauthenticate/`,
    method: 'put',
    skip: true,
  })

  const { fetch: callActivateIntegration, loading: isActivating } = useAxios({
    method: 'post',
    skip: true,
  })

  const { fetch: callDeactivateIntegration } = useAxios({
    method: 'put',
    skip: true,
  })

  const { fetch: callDeleteCycle } = useAxios({
    method: 'delete',
    skip: true,
  })

  const { data: oauthToken } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/oauth/`,
    skip: R.prop('authentication_type', connector) !== 'oauth2',
  })

  const { fetch: oauthAuthenication } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/authenticate/oauth/`,
    method: 'put',
    skip: true,
  })

  const {
    fetch: callUpdateAccountConnectorProperties,
    loading: isUpdatingAccountConnectorProperties,
  } = useAxios({
    url: `/cyclr/account/connectors/${connectorId}/properties/update/`,
    method: 'put',
    skip: true,
  })

  let oauthRedirectUrl: string = ''
  if (oauthToken) {
    const callbackUrl = `${window.location.origin}${location.pathname}?connector_authenticated=true`
    oauthRedirectUrl = `https://${process.env.REACT_APP_CYCLR_SERVICE_DOMAIN}/connectorauth/updateaccountconnectoroauth?id=${oauthToken.account_connector_id}&token=${oauthToken.signin_token}&targetOrigin=${callbackUrl}`
  }

  const saveIntegrationLog = useCallback(
    (logActionName, response, template) => {
      const templateData = {
        connector_template_id: template?.template_id || '',
        connector_template_name: template?.name || '',
      }

      const baseLogData = {
        action: logActionName,
        target: `com_${companyId}`,
        requestBody: {
          connector_id: connectorId,
          connector_name: connectorLabel,
          ...(template ? templateData : {}),
        },
      }

      const isSuccess = response?.status === 200

      const logData = isSuccess
        ? {
            ...baseLogData,
            statusCode: response.status,
          }
        : {
            ...baseLogData,
            statusCode: response?.status,
            errorResponseBody: response?.data,
          }

      saveUserActivityLog(logData)
    },
    [companyId, connectorId, connectorLabel, saveUserActivityLog]
  )

  useEffect(() => {
    const isOauthType = R.prop('authentication_type', connector) === 'oauth2'
    if (isOauthType && queryParams.get('connector_authenticated') === 'true') {
      queryParams.delete('connector_authenticated')
      queryParams.delete('result')
      history.replace({
        search: queryParams.toString(),
      })

      oauthAuthenication()
        .then((response) => {
          toast.success(`Woohoo! Simplesat is now connected with ${connector.connector_label}`)

          mutate(
            `/cyclr/account/connectors/${connectorId}/`,
            (state) => {
              return { ...state, is_authenticated: true }
            },
            false
          )
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, response)
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, error.response)
        })
    }
  }, [connector, connectorId, history, oauthAuthenication, queryParams, saveIntegrationLog])

  function authenicateConnector(authData) {
    if (connector.authentication_type === 'basic') {
      const { username, password, properties } = authData
      callBasicAuthenication({
        data: {
          username,
          password,
          properties,
        },
      })
        .then((response) => {
          const data = response.data
          toast.success(`Woohoo! Simplesat is now connected with ${connector.connector_label}`)
          mutate(
            `/cyclr/account/connectors/${connectorId}/`,
            (state) => {
              return { ...state, ...data }
            },
            false
          )
          setIsAuthenticateSuccessful(true)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, response)
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
          setIsAuthenticateSuccessful(false)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, error.response)
        })
    }

    if (connector.authentication_type === 'api_key') {
      const { apiKey, properties } = authData

      callApiKeyAuthenication({
        data: {
          api_key: apiKey,
          properties,
        },
      })
        .then((response) => {
          const data = response.data

          toast.success(`Woohoo! Simplesat is now connected with ${connector.connector_label}`)
          mutate(
            `/cyclr/account/connectors/${connectorId}/`,
            (state) => {
              return { ...state, ...data }
            },
            false
          )
          setIsAuthenticateSuccessful(true)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, response)
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
          setIsAuthenticateSuccessful(false)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, error.response)
        })
    }

    if (connector.authentication_type === 'oauth2') {
      const { properties } = authData
      callUpdateAccountConnectorProperties({
        data: {
          properties,
        },
      })
        .then(() => {
          if (oauthRedirectUrl) {
            window.location.href = oauthRedirectUrl
          }
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
        })
    }

    if (connector.authentication_type === 'oauth2_password_credential') {
      const { properties } = authData
      callOauth2PasswordCredentialAuthen({
        data: {
          properties,
        },
      })
        .then((response) => {
          const data = response?.data
          if (data?.is_authenticated) {
            toast.success(`Woohoo! Simplesat is now connected with ${connector.connector_label}`)
            mutate(
              `/cyclr/account/connectors/${connectorId}/`,
              (state) => {
                return { ...state, ...data }
              },
              false
            )
            setIsAuthenticateSuccessful(true)
            saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, response)
          } else {
            toast.error(
              'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
            )
            setIsAuthenticateSuccessful(false)
          }
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, error.response)
        })
    }

    if (connector.authentication_type === 'none') {
      const { properties } = authData
      callNoneAuthenication({
        data: {
          properties,
        },
      })
        .then((response) => {
          const data = response.data
          toast.success(`Woohoo! Simplesat is now connected with ${connector.connector_label}`)
          mutate(
            `/cyclr/account/connectors/${connectorId}/`,
            (state) => {
              return { ...state, ...data }
            },
            false
          )
          setIsAuthenticateSuccessful(true)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, response)
        })
        .catch((error) => {
          toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
          setIsAuthenticateSuccessful(false)
          saveIntegrationLog(INTEGRATION_ACTION_NAME.authenticated_integration, error.response)
        })
    }
  }

  function deactivateIntegration(cycle_id) {
    callDeactivateIntegration({
      url: `cyclr/cycles/${cycle_id}/deactivate/`,
    })
      .then(() => {
        toast.success('Integration deactivated.')
        mutate(
          `/cyclr/account/connectors/${connectorId}/`,
          (state) => {
            const index = R.findIndex(R.propEq('cycle_id', cycle_id))(state.templates)
            const disabledActiveStatusTemplates = R.assocPath(
              [index, 'is_active'],
              false,
              state.templates
            )
            return { ...state, templates: disabledActiveStatusTemplates }
          },
          false
        )
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  function deleteIntegration(cycle_id, close, setLoadingModal) {
    callDeleteCycle({
      url: `cyclr/cycles/${cycle_id}/delete/`,
    })
      .then(() => {
        toast.success(
          'The integration has successfully been reset to default values. A clean slate!'
        )
        setLoadingModal(false)
        close() // close delete modal
        mutate(
          `/cyclr/account/connectors/${connectorId}/`,
          (state) => {
            const index = R.findIndex(R.propEq('cycle_id', cycle_id))(state.templates)
            const disabledActiveStatusTemplates = R.assocPath(
              [index, 'is_active'],
              false,
              state.templates
            )
            disabledActiveStatusTemplates[index].cycle_id = null
            return { ...state, templates: disabledActiveStatusTemplates }
          },
          false
        )
      })
      .catch((error) => {
        setLoadingModal(false)
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  function activateIntegration(template: IntegrationTemplate) {
    callActivateIntegration({
      url: `cyclr/account/connectors/${connectorId}/templates/${template.template_id}/activate/`,
    })
      .then((response) => {
        const data = response.data

        toast.success('Integration activated!')
        mutate(
          `/cyclr/account/connectors/${connectorId}/`,
          (state) => {
            const index = R.findIndex(R.propEq('template_id', template.template_id))(
              state.templates
            )
            const updatedCycleIdTemplates = R.assocPath(
              [index, 'cycle_id'],
              data.cycle_id,
              state.templates
            )
            const newTemplates = R.assocPath([index, 'is_active'], true, updatedCycleIdTemplates)
            return { ...state, templates: newTemplates }
          },
          false
        )
        saveIntegrationLog(INTEGRATION_ACTION_NAME.connected_integration, response, template)
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
        saveIntegrationLog(INTEGRATION_ACTION_NAME.connected_integration, error.response, template)
      })
  }

  function revokeAccess() {
    callRevokeAccess()
      .then(() => {
        toast.success(`Successfully revoked access with ${connector.connector_label}.`)
        mutate(
          `/cyclr/account/connectors/${connectorId}/`,
          (state) => {
            const deactivatedTemplates = (state.templates || []).map((template) => ({
              ...template,
              is_active: false,
            }))
            return {
              ...state,
              is_authenticated: false,
              templates: deactivatedTemplates,
            }
          },
          false
        )
      })
      .catch((error) => {
        toast.error(parseErrorMessage(error.response.data, error.response.status, error.code))
      })
  }

  if (!connector) {
    return <Loader withContainer />
  }

  const templates = groupByTemplatesByIntegrationLevel(connector.templates)

  return (
    <EmbeddingCyclrIntegration
      connectorId={connector.id}
      connectorName={connector.connector_label}
      connectorDescription={connector.connector_description}
      connectorImage={connector.connector_image}
      isAuthenticated={connector.is_authenticated}
      accountWideIntegrationTemplates={R.prop('account_wide_integration', templates) || []}
      surveyIntegrationTemplates={R.prop('survey_integration', templates) || []}
      ticketIntegrationTemplates={R.prop('ticket_integration', templates) || []}
      authenticatioType={connector.authentication_type}
      authenticationSettings={connector.authentication_settings}
      connectorProperties={connector.connector_properties}
      activateIntegration={activateIntegration}
      deactivateIntegration={deactivateIntegration}
      deleteIntegration={deleteIntegration}
      authenicateConnector={authenicateConnector}
      revokeAccess={revokeAccess}
      isActivating={isActivating}
      isAuthenticateSuccessful={isAuthenticateSuccessful}
      isAuthenticating={
        isBasicAuthenticating ||
        isApiKeyAuthenticating ||
        isOauth2PasswordCredentialAuthenticating ||
        isNoneAuthenticating ||
        isUpdatingAccountConnectorProperties
      }
      oauthRedirectUrl={oauthRedirectUrl}
      isRevoking={isRevoking}
    />
  )
}

type groupByTemplatesByIntegrationLevelType = {
  account_wide_integration: IntegrationTemplate[]
  survey_integration: IntegrationTemplate[]
  ticket_integration: IntegrationTemplate[]
}

function groupByTemplatesByIntegrationLevel(templates): groupByTemplatesByIntegrationLevelType {
  const sortedTemplates = R.sortBy(R.prop('order'))(templates)
  return R.groupBy(R.prop('integration_level'), sortedTemplates)
}



// WEBPACK FOOTER //
// ./src/components/EmbeddingCyclrIntegration/container.tsx